/* eslint-disable no-param-reassign */
import _axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { staticSelector, store } from '../redux';
import { customSelectors, setApiError } from '../redux/features/custom.slice';
import { selectMilesCustomerIdFromDataStorage } from '../redux/features/dataStorage.redux';
import { loadingActions } from '../redux/features/loading.slice';
import { decorateAxiosRequestConfigWithToken, removeAuthorizationData } from './authorization';
import { trackError } from './tracking';

declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressErrorInterceptor?: boolean;
  }
}

export const SUPPRESS_ERROR_INTERCEPTOR: AxiosRequestConfig = { suppressErrorInterceptor: true };

const { REACT_APP_API_GATEWAY, REACT_APP_BACKEND_BRAND } = process.env;
const extendedErrorAllowed = ['dev', 'int'].includes(process.env.REACT_APP_STAGE || '');

export const axios = _axios.create({
  baseURL: REACT_APP_API_GATEWAY,
});

/**
 * - Adds Miles token in authorization header.
 * - Token is forwarded up to BFT for authorization with Miles services.
 */
export const axiosWithToken = _axios.create({
  baseURL: REACT_APP_API_GATEWAY,
});

const decorateAxiosRequestWithHybridJourneyHeaders = (config: InternalAxiosRequestConfig) => {
  const isHybridJourney = staticSelector(customSelectors.selectIsHybridJourney);
  const milesCustomerId = staticSelector(selectMilesCustomerIdFromDataStorage);

  if (isHybridJourney) {
    config.headers['hybrid-journey'] = isHybridJourney;
  }

  if (milesCustomerId) {
    config.headers['miles-customer-id'] = milesCustomerId;
  }

  return config;
};

axiosWithToken.interceptors.request.use(
  async (config) => {
    if (staticSelector(customSelectors.selectIsHybridJourney)) {
      decorateAxiosRequestWithHybridJourneyHeaders(config);
    } else {
      await decorateAxiosRequestConfigWithToken(config);
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

const forwardErrorForDisplay = (error: any, extended: boolean = false) => {
  store.dispatch(setApiError({ hasError: true, errorData: error?.response?.data, extended }));
  // eslint-disable-next-line no-console
  console.warn(
    `Error intercepted and supressed for display in error modal.` +
      `Any logic included in subsequent 'catch-es' which would normally run, will not run this time.`
  );
};

// Shows the global error modal from a call that opted out of the interceptor but still needs it.
export const displayApiError = (error: unknown) => forwardErrorForDisplay(error, extendedErrorAllowed);

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    forwardErrorForDisplay(error, extendedErrorAllowed);
    store.dispatch(loadingActions.setLoading(false));
    store.dispatch(loadingActions.setLoading(false));
    store.dispatch(loadingActions.endAllContextualLoading());

    if (error.response?.status && ['prod'].includes(process.env.REACT_APP_STAGE || '')) {
      trackError(error.response?.status);
    }

    return Promise.resolve();
  }
);

axiosWithToken.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      removeAuthorizationData();
    }

    if (error.response?.status && ['prod'].includes(process.env.REACT_APP_STAGE || '')) {
      trackError(error.response?.status);
    }

    if (error.config?.suppressErrorInterceptor) {
      return Promise.reject(error);
    }

    forwardErrorForDisplay(error, extendedErrorAllowed);

    return Promise.resolve();
  }
);

export const getFullUrl = (urlFragment: string, noBrand = false) => {
  return `${REACT_APP_API_GATEWAY}${noBrand ? '' : '/' + REACT_APP_BACKEND_BRAND}/${urlFragment}`;
};

export const getFullUrlMiles = (urlFragment: string, noBrand = false) => {
  return `${REACT_APP_API_GATEWAY}/miles${noBrand ? '' : '/' + REACT_APP_BACKEND_BRAND}/${urlFragment}`;
};

export const callFes = {
  post<T extends Record<string, any>>(urlFragment: string, data: T, noBrand = false) {
    return axios.post(getFullUrl(urlFragment, noBrand), data).then(({ data }) => data);
  },

  patch<R = any, T extends Record<string, any> = any>(urlFragment: string, data: T, noBrand = false) {
    return axios.patch<R>(getFullUrl(urlFragment, noBrand), data).then(({ data }) => data);
  },

  put<T extends Record<string, any>>(urlFragment: string, data: T, noBrand = false) {
    return axios.put(getFullUrl(urlFragment, noBrand), data).then(({ data }) => data);
  },

  get(urlFragment: string, noBrand = false, params = {}) {
    return axios.get(getFullUrl(urlFragment, noBrand), params).then(({ data }) => data);
  },

  delete(urlFragment: string, noBrand = false) {
    return axios.delete(getFullUrl(urlFragment, noBrand)).then(({ data }) => data);
  },

  postMiles<T extends Record<string, any>>(urlFragment: string, data?: T, noBrand = false, config = {}) {
    return axiosWithToken.post(getFullUrlMiles(urlFragment, noBrand), data, config).then(({ data }) => data);
  },

  patchMiles<T extends Record<string, any>>(urlFragment: string, data: T, noBrand = false, config = {}) {
    return axiosWithToken.patch(getFullUrlMiles(urlFragment, noBrand), data, config).then(({ data }) => data);
  },

  putMiles<T extends Record<string, any>>(urlFragment: string, data: T, noBrand = false, config = {}) {
    return axiosWithToken.put(getFullUrlMiles(urlFragment, noBrand), data, config).then(({ data }) => data);
  },

  getMiles<T = any>(urlFragment: string, noBrand = false, config = {}) {
    return axiosWithToken.get<T>(getFullUrlMiles(urlFragment, noBrand), config).then(({ data }) => data);
  },

  deleteMiles(urlFragment: string, noBrand = false, config = {}) {
    return axiosWithToken.delete(getFullUrlMiles(urlFragment, noBrand), config).then(({ data }) => data);
  },
};

type anyHandler = (...args: any[]) => Promise<any>;

/**
 * - Same as `callFes` just creates contextual loader on start and removes it
 * when request promise finishes regardless of the result.
 */
export const callFesWithLoading = Object.entries(callFes).reduce(
  (acc, [operationName, handler]: [string, anyHandler]) => {
    return {
      ...acc,
      [operationName]: (...args: any[]) => {
        const loadingId = store.dispatch(loadingActions.startContextualLoading()).payload.id;
        return handler(...args).finally(() => {
          store.dispatch(loadingActions.endContextualLoading(loadingId));
        });
      },
    };
  },
  {}
) as typeof callFes;
